import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { differenceInDays, isPast, format } from 'date-fns';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const AddPaymentMethodForm = ({ onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');
    try {
      const { error: stripeError, paymentMethod } = await stripe?.createPaymentMethod({ elements });
      if (stripeError) {
        setError(stripeError?.message || 'Payment method failed.');
        setProcessing(false);
        return;
      }
      const { error: dbError } = await supabase?.from('org_subscriptions')?.update({
        payment_method_added: true,
        stripe_payment_method_id: paymentMethod?.id,
        status: 'active',
        account_suspended_at: null,
        suspension_reason: null,
        updated_at: new Date()?.toISOString()
      })?.not('id', 'is', null);
      if (dbError) throw dbError;
      onSuccess?.();
    } catch (err) {
      setError(err?.message || 'Failed to add payment method.');
      onError?.(err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {processing ? (
          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
        ) : (
          <><Icon name="Lock" size={16} color="currentColor" /> Add Payment Method</>
        )}
      </button>
    </form>
  );
};

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    pricePerUser: 19,
    pricePerUserAnnual: 15,
    maxUsers: 5,
    color: 'border-gray-200',
    badge: '',
    features: ['Up to 5 users', 'Ticket & job management', 'Mobile technician app', 'Basic reporting', 'Email support']
  },
  {
    id: 'professional',
    name: 'Professional',
    pricePerUser: 29,
    pricePerUserAnnual: 23,
    maxUsers: 25,
    color: 'border-primary',
    badge: 'Most Popular',
    features: ['Up to 25 users', 'Everything in Starter', 'GPS location tracking', 'Signature capture', 'Invoice & quote management', 'Customer portal', 'Priority support']
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    pricePerUser: 49,
    pricePerUserAnnual: 39,
    maxUsers: 999,
    color: 'border-purple-300',
    badge: 'Unlimited',
    features: ['Unlimited users', 'Everything in Professional', 'Custom integrations', 'Advanced analytics', 'Dedicated account manager', 'SLA guarantee', '24/7 phone support']
  }
];

// Mock subscription history
const MOCK_HISTORY = [
  { id: 1, date: '2026-02-01', event: 'Plan Changed', description: 'Upgraded from Starter to Professional', amount: '$145.00', status: 'completed', icon: 'ArrowUpCircle', color: 'text-green-600 bg-green-50' },
  { id: 2, date: '2026-01-01', event: 'Payment Processed', description: 'Monthly billing - Professional Plan (5 users)', amount: '$145.00', status: 'paid', icon: 'CreditCard', color: 'text-blue-600 bg-blue-50' },
  { id: 3, date: '2025-12-01', event: 'Payment Processed', description: 'Monthly billing - Starter Plan (5 users)', amount: '$95.00', status: 'paid', icon: 'CreditCard', color: 'text-blue-600 bg-blue-50' },
  { id: 4, date: '2025-11-15', event: 'Trial Started', description: '14-day free trial activated', amount: '$0.00', status: 'completed', icon: 'Zap', color: 'text-purple-600 bg-purple-50' },
];

const PlanChangeModal = ({ currentPlan, selectedPlan, billingCycle, userCount, calculateTotal, onConfirm, onCancel }) => {
  const currentPlanData = PLANS?.find(p => p?.id === currentPlan);
  const newPlanData = PLANS?.find(p => p?.id === selectedPlan);
  const isUpgrade = PLANS?.findIndex(p => p?.id === selectedPlan) > PLANS?.findIndex(p => p?.id === currentPlan);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-card rounded-2xl shadow-elevation-3 w-full max-w-md">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isUpgrade ? 'bg-green-100' : 'bg-orange-100'}`}>
              <Icon name={isUpgrade ? 'ArrowUpCircle' : 'ArrowDownCircle'} size={20} color={isUpgrade ? '#16a34a' : '#ea580c'} />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground">{isUpgrade ? 'Upgrade' : 'Downgrade'} Plan</h2>
              <p className="text-sm text-muted-foreground">Review your plan change</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 p-4 bg-muted rounded-xl">
            <div className="flex-1">
              <p className="text-xs text-muted-foreground mb-1">Current Plan</p>
              <p className="font-semibold text-foreground capitalize">{currentPlanData?.name}</p>
              <p className="text-sm text-muted-foreground">${billingCycle === 'annual' ? currentPlanData?.pricePerUserAnnual : currentPlanData?.pricePerUser}/user/mo</p>
            </div>
            <Icon name="ArrowRight" size={20} color="var(--color-muted-foreground)" />
            <div className="flex-1 text-right">
              <p className="text-xs text-muted-foreground mb-1">New Plan</p>
              <p className="font-semibold text-foreground capitalize">{newPlanData?.name}</p>
              <p className="text-sm text-primary">${billingCycle === 'annual' ? newPlanData?.pricePerUserAnnual : newPlanData?.pricePerUser}/user/mo</p>
            </div>
          </div>
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-foreground">New {billingCycle === 'annual' ? 'Annual' : 'Monthly'} Total</p>
                <p className="text-xs text-muted-foreground">{userCount} users × ${billingCycle === 'annual' ? newPlanData?.pricePerUserAnnual : newPlanData?.pricePerUser}/user</p>
              </div>
              <p className="text-2xl font-bold text-primary">${calculateTotal(selectedPlan, userCount)}</p>
            </div>
            {billingCycle === 'annual' && (
              <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                <Icon name="Tag" size={12} color="currentColor" />
                Annual billing saves ${((newPlanData?.pricePerUser - newPlanData?.pricePerUserAnnual) * userCount * 12)?.toFixed(0)}/year
              </p>
            )}
          </div>
          {isUpgrade ? (
            <p className="text-sm text-muted-foreground">Your plan will be upgraded immediately. You'll be charged the prorated difference for the current billing period.</p>
          ) : (
            <p className="text-sm text-muted-foreground">Your plan will be downgraded at the end of the current billing period. You'll retain access to current features until then.</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-smooth">Cancel</button>
          <button
            onClick={onConfirm}
            className={`px-5 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 ${isUpgrade ? 'bg-green-600' : 'bg-orange-500'}`}
          >
            <Icon name={isUpgrade ? 'ArrowUpCircle' : 'ArrowDownCircle'} size={15} color="white" />
            Confirm {isUpgrade ? 'Upgrade' : 'Downgrade'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SubscriptionBilling = () => {
  const [subscription, setSubscription] = useState(null);
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [userCount, setUserCount] = useState(5);
  const [billingForm, setBillingForm] = useState({ billing_name: '', billing_email: '', billing_address: '' });
  const [activeTab, setActiveTab] = useState('plan');
  const [clientSecret, setClientSecret] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([
    { id: 'pm_1', brand: 'Visa', last4: '4242', expMonth: 12, expYear: 2027, isDefault: true },
  ]);
  const [showAddPayment, setShowAddPayment] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orgRes, subRes, membersRes] = await Promise.all([
        supabase?.from('organizations')?.select('*')?.limit(1)?.single(),
        supabase?.from('org_subscriptions')?.select('*')?.limit(1)?.single(),
        supabase?.from('org_members')?.select('*')?.order('created_at', { ascending: false })
      ]);
      if (orgRes?.data) setOrg(orgRes?.data);
      if (subRes?.data) {
        setSubscription(subRes?.data);
        setSelectedPlan(subRes?.data?.plan || 'professional');
        setUserCount(subRes?.data?.max_users || 5);
        setBillingForm({
          billing_name: subRes?.data?.billing_name || '',
          billing_email: subRes?.data?.billing_email || '',
          billing_address: subRes?.data?.billing_address || ''
        });
      }
      setMembers(membersRes?.data || []);
    } catch (err) {
      console.error('Fetch error:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getPlanDetails = (planId) => PLANS?.find(p => p?.id === planId) || PLANS?.[1];

  const calculateMonthlyTotal = (planId, count) => {
    const plan = getPlanDetails(planId);
    const price = billingCycle === 'annual' ? plan?.pricePerUserAnnual : plan?.pricePerUser;
    return (price * count)?.toFixed(2);
  };

  const handlePlanSelect = (planId) => {
    if (planId === selectedPlan) return;
    if (subscription?.plan && subscription?.plan !== planId) {
      setPendingPlan(planId);
      setShowPlanChangeModal(true);
    } else {
      setSelectedPlan(planId);
      setUserCount(Math.min(userCount, getPlanDetails(planId)?.maxUsers === 999 ? userCount : getPlanDetails(planId)?.maxUsers));
    }
  };

  const handleConfirmPlanChange = async () => {
    setShowPlanChangeModal(false);
    setSelectedPlan(pendingPlan);
    setUserCount(Math.min(userCount, getPlanDetails(pendingPlan)?.maxUsers === 999 ? userCount : getPlanDetails(pendingPlan)?.maxUsers));
    setPendingPlan(null);
    await handleSavePlan(pendingPlan);
  };

  const handleSavePlan = async (overridePlan) => {
    setSaving(true);
    setError('');
    setSaved(false);
    const planToSave = overridePlan || selectedPlan;
    try {
      const plan = getPlanDetails(planToSave);
      const price = billingCycle === 'annual' ? plan?.pricePerUserAnnual : plan?.pricePerUser;
      const payload = {
        plan: planToSave,
        max_users: userCount,
        price_per_user_monthly: price,
        current_user_count: members?.filter(m => m?.is_active)?.length || 1,
        ...billingForm,
        updated_at: new Date()?.toISOString()
      };
      if (subscription?.id) {
        const { error: err } = await supabase?.from('org_subscriptions')?.update(payload)?.eq('id', subscription?.id);
        if (err) throw err;
      } else if (org?.id) {
        const { data, error: err } = await supabase?.from('org_subscriptions')?.insert({
          ...payload,
          organization_id: org?.id,
          status: 'trialing'
        })?.select()?.single();
        if (err) throw err;
        setSubscription(data);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to update subscription.');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'active') return 'bg-green-100 text-green-700';
    if (status === 'trialing') return 'bg-blue-100 text-blue-700';
    if (status === 'past_due') return 'bg-red-100 text-red-700';
    if (status === 'cancelled') return 'bg-gray-100 text-gray-600';
    return 'bg-yellow-100 text-yellow-700';
  };

  const activeMembers = members?.filter(m => m?.is_active);
  const trialDaysLeft = subscription?.trial_ends_at && subscription?.status === 'trialing'
    ? Math.max(0, differenceInDays(new Date(subscription?.trial_ends_at), new Date()))
    : null;
  const isTrialExpired = subscription?.status === 'trialing' && subscription?.trial_ends_at && isPast(new Date(subscription?.trial_ends_at));
  const isSuspended = !!subscription?.account_suspended_at;

  const handleInitiatePayment = async () => {
    if (!billingForm?.billing_name || !billingForm?.billing_email) {
      setError('Please fill in billing name and email first.');
      setActiveTab('billing');
      return;
    }
    setLoadingPayment(true);
    setError('');
    try {
      const plan = getPlanDetails(selectedPlan);
      const amount = parseFloat(calculateMonthlyTotal(selectedPlan, userCount));
      const { data, error: fnError } = await supabase?.functions?.invoke('create-payment-intent', {
        body: {
          paymentData: {
            amount,
            currency: 'usd',
            tableName: 'org_subscriptions',
            description: `RIGJOCKEY ${plan?.name} Plan - ${userCount} users`,
          },
          customerInfo: {
            userId: null,
            firstName: billingForm?.billing_name?.split(' ')?.[0] || billingForm?.billing_name,
            lastName: billingForm?.billing_name?.split(' ')?.slice(1)?.join(' ') || '',
            email: billingForm?.billing_email,
            billing: { address_line_1: billingForm?.billing_address, country: 'US' }
          }
        }
      });
      if (fnError) throw fnError;
      setClientSecret(data?.clientSecret);
      setShowPaymentForm(true);
    } catch (err) {
      setError(err?.message || 'Failed to initialize payment.');
    } finally {
      setLoadingPayment(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setPaymentSuccess(true);
    setShowPaymentForm(false);
    await supabase?.from('org_subscriptions')?.update({
      status: 'active',
      plan: selectedPlan,
      max_users: userCount,
      payment_method_added: true,
      account_suspended_at: null,
      suspension_reason: null,
      ...billingForm,
      updated_at: new Date()?.toISOString()
    })?.eq('id', subscription?.id);
    fetchData();
  };

  const TABS = [
    { id: 'plan', label: 'Choose Plan', icon: 'Zap' },
    { id: 'users', label: 'Users', icon: 'Users' },
    { id: 'billing', label: 'Billing Info', icon: 'CreditCard' },
    { id: 'payment', label: 'Payment Methods', icon: 'Wallet' },
    { id: 'history', label: 'History', icon: 'Clock' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Subscription & Billing - RIGJOCKEY</title></Helmet>
      <Header />
      <main className="pt-6 pb-16 px-4 max-w-5xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="CreditCard" size={22} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Subscription & Billing</h1>
              <p className="text-sm text-muted-foreground">Manage your plan, users, and billing information</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current Status Banner */}
            {subscription && (
              <div className="bg-card rounded-xl border border-border p-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Icon name="Zap" size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Current Plan</p>
                      <p className="text-xl font-bold text-foreground capitalize">{subscription?.plan}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(subscription?.status)}`}>
                      {subscription?.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-foreground">{activeMembers?.length}</p>
                      <p className="text-xs text-muted-foreground">Active Users</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-foreground">{subscription?.max_users}</p>
                      <p className="text-xs text-muted-foreground">Max Users</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-primary">${calculateMonthlyTotal(subscription?.plan, subscription?.max_users)}</p>
                      <p className="text-xs text-muted-foreground">/ {billingCycle === 'annual' ? 'mo (annual)' : 'month'}</p>
                    </div>
                  </div>
                </div>

                {subscription?.trial_ends_at && subscription?.status === 'trialing' && !isTrialExpired && (
                  <div className={`mt-4 p-3 rounded-lg border ${
                    trialDaysLeft <= 3 ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <p className={`text-sm flex items-center gap-2 ${
                        trialDaysLeft <= 3 ? 'text-orange-700' : 'text-blue-700'
                      }`}>
                        <Icon name="Clock" size={14} color="currentColor" />
                        Free trial: <strong>{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining</strong>
                        {' '}(expires {format(new Date(subscription?.trial_ends_at), 'MMM d, yyyy')})
                      </p>
                      <button
                        onClick={() => setActiveTab('billing')}
                        className="text-xs px-3 py-1 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90"
                      >
                        Add Payment Method
                      </button>
                    </div>
                  </div>
                )}

                {isTrialExpired && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertOctagon" size={16} color="#dc2626" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-red-700">Trial Expired</p>
                        <p className="text-sm text-red-600 mt-0.5">Your 14-day free trial has ended. Add a payment method to reactivate your account.</p>
                      </div>
                      <button onClick={() => setActiveTab('billing')} className="shrink-0 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700">
                        Reactivate
                      </button>
                    </div>
                  </div>
                )}

                {isSuspended && (
                  <div className="mt-4 p-4 bg-gray-100 border border-gray-300 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Icon name="Lock" size={16} color="#6b7280" />
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Account Suspended</p>
                        <p className="text-sm text-gray-600 mt-0.5">{subscription?.suspension_reason || 'Account has been suspended.'} Add a payment method to restore access.</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2">
                  <Icon name={subscription?.payment_method_added ? 'CheckCircle' : 'AlertCircle'} size={14} color={subscription?.payment_method_added ? '#16a34a' : '#f59e0b'} />
                  <span className={`text-xs ${subscription?.payment_method_added ? 'text-green-600' : 'text-amber-600'}`}>
                    {subscription?.payment_method_added ? 'Payment method on file' : 'No payment method added — required after trial'}
                  </span>
                </div>
              </div>
            )}

            {/* Billing Cycle Toggle */}
            <div className="flex items-center justify-center">
              <div className="inline-flex items-center bg-muted rounded-xl p-1 gap-1">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    billingCycle === 'monthly' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    billingCycle === 'annual' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Annual
                  <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">Save 20%</span>
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-muted rounded-xl p-1 overflow-x-auto">
              {TABS?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-shrink-0 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab?.id ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={14} color="currentColor" />
                  <span className="hidden sm:inline">{tab?.label}</span>
                </button>
              ))}
            </div>

            {/* Plan Selection */}
            {activeTab === 'plan' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {PLANS?.map(plan => (
                    <div
                      key={plan?.id}
                      onClick={() => handlePlanSelect(plan?.id)}
                      className={`relative bg-card rounded-xl border-2 p-5 cursor-pointer transition-all hover:shadow-md ${
                        selectedPlan === plan?.id ? 'border-primary shadow-md' : plan?.color
                      }`}
                    >
                      {plan?.badge && (
                        <span className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-semibold ${
                          plan?.id === 'professional' ? 'bg-primary text-primary-foreground' : 'bg-purple-600 text-white'
                        }`}>
                          {plan?.badge}
                        </span>
                      )}
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-foreground">{plan?.name}</h3>
                        {selectedPlan === plan?.id && (
                          <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <Icon name="Check" size={12} color="white" />
                          </div>
                        )}
                      </div>
                      <div className="mb-1">
                        <span className="text-3xl font-bold text-foreground">${billingCycle === 'annual' ? plan?.pricePerUserAnnual : plan?.pricePerUser}</span>
                        <span className="text-sm text-muted-foreground">/user/mo</span>
                      </div>
                      {billingCycle === 'annual' && (
                        <p className="text-xs text-muted-foreground line-through mb-3">${plan?.pricePerUser}/user/mo billed monthly</p>
                      )}
                      {billingCycle === 'monthly' && <div className="mb-3" />}
                      <ul className="space-y-2">
                        {plan?.features?.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Icon name="Check" size={14} color="#16a34a" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* User Count Slider */}
                <div className="bg-card rounded-xl border border-border p-5">
                  <h3 className="font-semibold text-foreground mb-4">Configure Users</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1">
                      <label className="text-sm text-muted-foreground mb-2 block">
                        Number of users: <span className="font-bold text-foreground">{userCount}</span>
                      </label>
                      <input
                        type="range"
                        min={1}
                        max={getPlanDetails(selectedPlan)?.maxUsers === 999 ? 100 : getPlanDetails(selectedPlan)?.maxUsers}
                        value={userCount}
                        onChange={(e) => setUserCount(parseInt(e?.target?.value))}
                        className="w-full accent-primary"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>1 user</span>
                        <span>{getPlanDetails(selectedPlan)?.maxUsers === 999 ? '100+' : getPlanDetails(selectedPlan)?.maxUsers} users</span>
                      </div>
                    </div>
                    <div className="text-center min-w-[120px]">
                      <p className="text-3xl font-bold text-primary">${calculateMonthlyTotal(selectedPlan, userCount)}</p>
                      <p className="text-xs text-muted-foreground">per month</p>
                      {billingCycle === 'annual' ? (
                        <p className="text-xs text-green-600 font-medium">${(parseFloat(calculateMonthlyTotal(selectedPlan, userCount)) * 12)?.toFixed(2)}/yr</p>
                      ) : (
                        <p className="text-xs text-muted-foreground">${(parseFloat(calculateMonthlyTotal(selectedPlan, userCount)) * 12)?.toFixed(2)}/yr</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-lg font-bold text-foreground">{userCount}</p>
                      <p className="text-xs text-muted-foreground">Users</p>
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-lg font-bold text-foreground">${billingCycle === 'annual' ? getPlanDetails(selectedPlan)?.pricePerUserAnnual : getPlanDetails(selectedPlan)?.pricePerUser}</p>
                      <p className="text-xs text-muted-foreground">Per User</p>
                    </div>
                    <div className="bg-primary/10 rounded-lg p-3">
                      <p className="text-lg font-bold text-primary">${calculateMonthlyTotal(selectedPlan, userCount)}</p>
                      <p className="text-xs text-muted-foreground">{billingCycle === 'annual' ? 'Mo (Annual)' : 'Monthly'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="p-5 border-b border-border flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-foreground">Organization Members</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">{activeMembers?.length} active of {subscription?.max_users || 5} allowed</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 bg-muted rounded-full w-32 overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${Math.min(100, (activeMembers?.length / (subscription?.max_users || 5)) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {Math.round((activeMembers?.length / (subscription?.max_users || 5)) * 100)}%
                    </span>
                  </div>
                </div>
                {members?.length === 0 ? (
                  <div className="p-10 text-center">
                    <Icon name="Users" size={40} color="#9ca3af" />
                    <p className="mt-3 text-muted-foreground">No members yet. Add users in Personnel Management.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {members?.map(member => (
                      <div key={member?.id} className="flex items-center justify-between px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-primary">
                              {member?.full_name?.charAt(0)?.toUpperCase() || member?.email?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{member?.full_name || member?.email}</p>
                            <p className="text-xs text-muted-foreground">{member?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full capitalize">{member?.role}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            member?.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {member?.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Billing Info */}
            {activeTab === 'billing' && (
              <div className="bg-card rounded-xl border border-border p-6 space-y-4">
                <h3 className="font-semibold text-foreground">Billing Information</h3>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Billing Name / Company</label>
                  <input
                    type="text"
                    value={billingForm?.billing_name}
                    onChange={(e) => setBillingForm(prev => ({ ...prev, billing_name: e?.target?.value }))}
                    placeholder="Company LLC"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Billing Email</label>
                  <input
                    type="email"
                    value={billingForm?.billing_email}
                    onChange={(e) => setBillingForm(prev => ({ ...prev, billing_email: e?.target?.value }))}
                    placeholder="billing@company.com"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Billing Address</label>
                  <textarea
                    value={billingForm?.billing_address}
                    onChange={(e) => setBillingForm(prev => ({ ...prev, billing_address: e?.target?.value }))}
                    placeholder="1234 Main St, Houston, TX 77001"
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  />
                </div>
                <div className="bg-muted rounded-xl p-4">
                  <p className="text-sm font-medium text-foreground mb-2">Pricing Summary</p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground capitalize">{selectedPlan} Plan ({billingCycle})</span>
                      <span className="text-foreground">${billingCycle === 'annual' ? getPlanDetails(selectedPlan)?.pricePerUserAnnual : getPlanDetails(selectedPlan)?.pricePerUser}/user/mo</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Users</span>
                      <span className="text-foreground">{userCount}</span>
                    </div>
                    {billingCycle === 'annual' && (
                      <div className="flex justify-between text-green-600">
                        <span>Annual savings</span>
                        <span>-${((getPlanDetails(selectedPlan)?.pricePerUser - getPlanDetails(selectedPlan)?.pricePerUserAnnual) * userCount * 12)?.toFixed(2)}/yr</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t border-border pt-1.5 mt-1.5">
                      <span className="text-foreground">Monthly Total</span>
                      <span className="text-primary">${calculateMonthlyTotal(selectedPlan, userCount)}</span>
                    </div>
                  </div>
                </div>

                {showPaymentForm && clientSecret ? (
                  <div className="border border-border rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="CreditCard" size={16} color="var(--color-primary)" />
                      <h4 className="font-medium text-foreground">Payment Details</h4>
                      <div className="ml-auto flex items-center gap-1">
                        <Icon name="Lock" size={12} color="#16a34a" />
                        <span className="text-xs text-green-600">Secured</span>
                      </div>
                    </div>
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                      <AddPaymentMethodForm
                        onSuccess={handlePaymentSuccess}
                        onError={(err) => setError(err?.message)}
                      />
                    </Elements>
                  </div>
                ) : paymentSuccess ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                    <Icon name="CheckCircle" size={20} color="#16a34a" />
                    <div>
                      <p className="font-semibold text-green-700">Payment method added!</p>
                      <p className="text-sm text-green-600">Your subscription is now active.</p>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={handleInitiatePayment}
                    disabled={loadingPayment}
                    className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loadingPayment ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Loading...</>
                    ) : (
                      <><Icon name="CreditCard" size={16} color="currentColor" /> Add Payment Method & Activate</>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Payment Methods Tab */}
            {activeTab === 'payment' && (
              <div className="space-y-4">
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground">Payment Methods</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">Manage your saved payment methods</p>
                    </div>
                    <button
                      onClick={() => setShowAddPayment(!showAddPayment)}
                      className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      <Icon name="Plus" size={14} color="white" />
                      Add New
                    </button>
                  </div>

                  {paymentMethods?.length === 0 ? (
                    <div className="p-10 text-center">
                      <Icon name="CreditCard" size={40} color="#9ca3af" />
                      <p className="mt-3 text-muted-foreground">No payment methods saved.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {paymentMethods?.map(pm => (
                        <div key={pm?.id} className="flex items-center justify-between px-5 py-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-800 rounded-md flex items-center justify-center">
                              <Icon name="CreditCard" size={16} color="white" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground capitalize">{pm?.brand} •••• {pm?.last4}</p>
                              <p className="text-xs text-muted-foreground">Expires {pm?.expMonth}/{pm?.expYear}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {pm?.isDefault ? (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Default</span>
                            ) : (
                              <button
                                onClick={() => setPaymentMethods(prev => prev?.map(p => ({ ...p, isDefault: p?.id === pm?.id })))}
                                className="text-xs text-primary hover:underline"
                              >
                                Set Default
                              </button>
                            )}
                            <button
                              onClick={() => setPaymentMethods(prev => prev?.filter(p => p?.id !== pm?.id))}
                              className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 rounded-lg transition-smooth"
                            >
                              <Icon name="Trash2" size={14} color="currentColor" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showAddPayment && (
                    <div className="p-5 border-t border-border bg-muted/30">
                      <h4 className="text-sm font-semibold text-foreground mb-3">Add Payment Method</h4>
                      <p className="text-sm text-muted-foreground mb-4">Enter your billing info first, then click below to securely add a card via Stripe.</p>
                      <button
                        onClick={() => { setActiveTab('billing'); setShowAddPayment(false); }}
                        className="w-full py-2.5 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-primary/5 transition-smooth flex items-center justify-center gap-2"
                      >
                        <Icon name="ArrowRight" size={14} color="currentColor" />
                        Go to Billing Info to Add Card
                      </button>
                    </div>
                  )}
                </div>

                {/* Billing Cycle Management */}
                <div className="bg-card rounded-xl border border-border p-5">
                  <h3 className="font-semibold text-foreground mb-4">Billing Cycle</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setBillingCycle('monthly')}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        billingCycle === 'monthly' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Icon name="Calendar" size={18} color={billingCycle === 'monthly' ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                        {billingCycle === 'monthly' && <Icon name="CheckCircle" size={16} color="var(--color-primary)" />}
                      </div>
                      <p className="font-semibold text-foreground">Monthly</p>
                      <p className="text-xs text-muted-foreground mt-1">Billed every month. Cancel anytime.</p>
                    </button>
                    <button
                      onClick={() => setBillingCycle('annual')}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        billingCycle === 'annual' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Icon name="CalendarDays" size={18} color={billingCycle === 'annual' ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                        {billingCycle === 'annual' && <Icon name="CheckCircle" size={16} color="var(--color-primary)" />}
                      </div>
                      <p className="font-semibold text-foreground">Annual</p>
                      <p className="text-xs text-green-600 mt-1 font-medium">Save 20% vs monthly billing</p>
                    </button>
                  </div>
                  {billingCycle === 'annual' && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <Icon name="Tag" size={14} color="#16a34a" />
                      <p className="text-sm text-green-700">You save <strong>${((getPlanDetails(selectedPlan)?.pricePerUser - getPlanDetails(selectedPlan)?.pricePerUserAnnual) * userCount * 12)?.toFixed(2)}</strong> per year with annual billing.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Subscription History Tab */}
            {activeTab === 'history' && (
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="p-5 border-b border-border">
                  <h3 className="font-semibold text-foreground">Subscription History</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">Plan changes, payments, and billing events</p>
                </div>
                <div className="divide-y divide-border">
                  {MOCK_HISTORY?.map(item => (
                    <div key={item?.id} className="flex items-start gap-4 px-5 py-4">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${item?.color}`}>
                        <Icon name={item?.icon} size={16} color="currentColor" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-foreground">{item?.event}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            item?.status === 'paid' ? 'bg-green-100 text-green-700' :
                            item?.status === 'completed'? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                          }`}>{item?.status}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">{item?.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">{item?.date}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-foreground">{item?.amount}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t border-border bg-muted/30">
                  <p className="text-xs text-muted-foreground text-center">Showing last 12 months of billing history. Contact support for older records.</p>
                </div>
              </div>
            )}

            {/* Save Button */}
            {(activeTab === 'plan' || activeTab === 'billing') && (
              <div className="flex items-center justify-between">
                <div>
                  {error && (
                    <p className="text-sm text-red-600 flex items-center gap-1.5">
                      <Icon name="AlertCircle" size={14} color="currentColor" /> {error}
                    </p>
                  )}
                  {saved && (
                    <p className="text-sm text-green-600 flex items-center gap-1.5">
                      <Icon name="CheckCircle" size={14} color="currentColor" /> Subscription updated!
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleSavePlan()}
                  disabled={saving}
                  className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center gap-2"
                >
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                  ) : (
                    <><Icon name="Save" size={16} color="currentColor" /> Save Subscription</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Plan Change Confirmation Modal */}
      {showPlanChangeModal && pendingPlan && (
        <PlanChangeModal
          currentPlan={subscription?.plan || selectedPlan}
          selectedPlan={pendingPlan}
          billingCycle={billingCycle}
          userCount={userCount}
          calculateTotal={calculateMonthlyTotal}
          onConfirm={handleConfirmPlanChange}
          onCancel={() => { setShowPlanChangeModal(false); setPendingPlan(null); }}
        />
      )}
    </div>
  );
};

export default SubscriptionBilling;
